import { createContext, useContext, useState, useEffect } from 'react';
import {
  isAuthenticated,
  login as apiLogin,
  register as apiRegister,
  logout as apiLogout,
  verifyMfa as apiVerifyMfa,
  decodeCurrentUser,
  refreshAuthToken,
} from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [isLoggedIn, setIsLoggedIn] = useState(isAuthenticated());
  const [user, setUser] = useState(() => {
    const decoded = decodeCurrentUser();
    if (!decoded) return null;
    const trialEndsAt = localStorage.getItem('trialEndsAt') || null;
    return { ...decoded, trialEndsAt };
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const authenticated = isAuthenticated();
    setIsLoggedIn(authenticated);
    if (authenticated) {
      const decoded = decodeCurrentUser();
      const trialEndsAt = localStorage.getItem('trialEndsAt') || null;
      setUser(decoded ? { ...decoded, trialEndsAt } : null);

      // Rolling-session pattern: every page load refreshes the JWT, extending
      // the 30-day window. Result: an active user is never asked to sign in
      // again unless they go 30+ days idle. This is the SaaS industry default
      // (Stripe, Linear, GitHub all do this).
      //
      // Fire-and-forget — refreshUser() updates user state with the
      // server-side fresh data (including avatarUrl, latest tier, etc.)
      // and silently swallows any error so transient API failures don't
      // log the user out. If the token is genuinely expired, fetchApi's
      // 401 handler will surface it via redirect.
      refreshAuthToken()
        .then(response => {
          if (response?.success && response.data?.user) {
            const { user: u, trialEndsAt: te } = response.data;
            if (te) localStorage.setItem('trialEndsAt', te);
            setUser({ ...u, trialEndsAt: te || null });
            setIsLoggedIn(true);
          }
        })
        .catch(() => { /* silenced — see fetchApi for hard-401 handling */ });
    } else {
      setUser(null);
    }
  }, []);

  const login = async ({ email, password } = {}) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiLogin({ email, password });
      if (response.success && response.mfaPending) {
        // Return mfaPending — LoginPage handles the second step
        return { mfaPending: true, mfaToken: response.mfaToken };
      }
      if (response.success) {
        setIsLoggedIn(true);
        const decoded = decodeCurrentUser();
        const trialEndsAt = response.data?.trialEndsAt || null;
        if (trialEndsAt) localStorage.setItem('trialEndsAt', trialEndsAt);
        else localStorage.removeItem('trialEndsAt');
        setUser(decoded ? { ...decoded, trialEndsAt } : null);
        return { success: true };
      }
      const errorMsg = typeof response.error === 'string'
        ? response.error
        : response.error?.message || 'Login failed';
      setError(errorMsg);
      return { success: false };
    } catch (err) {
      setError(err.message || 'Login failed');
      return { success: false };
    } finally {
      setIsLoading(false);
    }
  };

  const verifyMfa = async ({ mfaToken, code }) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiVerifyMfa({ mfaToken, code });
      if (response.success) {
        setIsLoggedIn(true);
        setUser(decodeCurrentUser());
        return { success: true };
      }
      const errorMsg = response.error?.message || 'Invalid code';
      setError(errorMsg);
      return { success: false };
    } catch (err) {
      setError(err.message || 'Verification failed');
      return { success: false };
    } finally {
      setIsLoading(false);
    }
  };

  const register = async ({ email, name, password, referredBy = null }) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiRegister({ email, name, password, referredBy });
      if (response.success) {
        setIsLoggedIn(true);
        const decoded = decodeCurrentUser();
        const trialEndsAt = response.data?.trialEndsAt || null;
        if (trialEndsAt) localStorage.setItem('trialEndsAt', trialEndsAt);
        else localStorage.removeItem('trialEndsAt');
        setUser(decoded ? { ...decoded, trialEndsAt } : null);
        return { success: true, trialEndsAt };
      } else {
        const errorMsg = response.error?.message || 'Registration failed';
        setError(errorMsg);
        return { success: false, error: errorMsg };
      }
    } catch (err) {
      const msg = err.message || 'Registration failed';
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setIsLoading(false);
    }
  };

  const refreshUser = async () => {
    try {
      const response = await refreshAuthToken();
      if (response.success && response.data?.user) {
        const { user: u, trialEndsAt } = response.data;
        if (trialEndsAt) localStorage.setItem('trialEndsAt', trialEndsAt);
        else localStorage.removeItem('trialEndsAt');
        setUser({ ...u, trialEndsAt: trialEndsAt || null });
        // CRITICAL: also flip isLoggedIn to true. Without this, AuthCallback
        // can persist a token + user but ProtectedRoute (which checks
        // isLoggedIn, not user) still bounces to /login. This was the cause
        // of the OAuth redirect loop on first sign-in.
        setIsLoggedIn(true);
      }
    } catch {}
  };

  const logout = async () => {
    try {
      await apiLogout();
    } finally {
      setIsLoggedIn(false);
      setUser(null);
      localStorage.removeItem('trialEndsAt');
    }
  };

  return (
    <AuthContext.Provider value={{ isLoggedIn, user, isLoading, error, login, verifyMfa, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Returns true if the signed-in user holds `permission` (from RBAC). The
 * permission catalog is in backend/src/shared/permissions.js and travels
 * down on user.permissions. Owners always return true.
 *
 * If no permission is passed, returns whether the user has ANY admin role
 * (handy for showing/hiding the Admin Center link wholesale).
 */
export function usePermission(permission) {
  const ctx = useContext(AuthContext);
  if (!ctx?.user) return false;
  const u = ctx.user;
  if (u.role === 'admin') return true; // legacy super-admin = Owner
  const perms = Array.isArray(u.permissions) ? u.permissions : [];
  const roles = Array.isArray(u.roles) ? u.roles : [];
  if (roles.includes('owner')) return true;
  if (!permission) return perms.length > 0 || roles.length > 0;
  return perms.includes(permission);
}

/**
 * Conditionally renders children only if the user holds `permission`.
 * Use for whole sections; for inline buttons, prefer `usePermission`.
 */
export function RequirePermission({ permission, fallback = null, children }) {
  const allowed = usePermission(permission);
  return allowed ? children : fallback;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
